// A view over a forecast stream that works whichever shape the forecaster speaks.
//
// There are two producers in the world right now and there will be both for a
// while: the deployed forecast server, which emits `progress`/`done` and nothing
// else, and a producer that also emits `update` frames carrying the typed
// `sooth-idl` progress vocabulary. A consumer that only understands one of them
// either breaks on the old server or ignores everything new, so this understands
// both and says which it got.
//
// Feed it the frames `parseSSEStream` already yields — it passes unknown event
// types through on purpose, which is what lets `update` reach here at all:
//
//   let view = EMPTY_VIEW;
//   for await (const frame of parseSSEStream(body, signal)) {
//     view = applyFrame(view, frame);
//     render(view);
//   }
//
// Deliberately small. `doc` is the whole folded document from the contract's own
// `applyUpdate`, so anything richer — triggers, the activity transcript, the
// report, revision history — is already in there to render without this file
// growing a field per idea.
import {
  EMPTY_FORECAST,
  applyUpdate,
  massOf,
  outcomeLabels,
  parseUpdate,
  type ForecastInProgress,
  type RunOutcome,
} from "@sooth-labs/idl";

/** Which producer we turned out to be talking to. */
export type ForecastShape = "legacy" | "typed";

/** The headline, in whichever form the question calls for. */
export type Headline =
  | { kind: "probability"; value: number }
  | { kind: "distribution"; labels: string[]; mass: number[] };

export interface ProgressView {
  /**
   * `legacy` until an `update` frame arrives, then `typed` forever.
   *
   * Read this to decide whether the rich surface is worth rendering: on a legacy
   * producer `doc` stays empty and there is nothing but a bar and a number.
   */
  shape: ForecastShape;
  /** 0-100 from the legacy frames, or null if this producer sends none. */
  percent: number | null;
  /** The producer's own label for what it is doing, when it says. */
  stage: string | null;
  done: boolean;
  /** How the run ended, when the producer says outright (typed only). */
  outcome: RunOutcome | null;
  error: string | null;
  headline: Headline | null;
  /**
   * The folded document — empty on a legacy producer.
   *
   * This is `sooth-idl`'s own `ForecastInProgress`, so triggers, `activity`,
   * `report`, `unknown` and the rest are here already. Render from it directly
   * rather than copying fields up into this view.
   */
  doc: ForecastInProgress;
}

export const EMPTY_VIEW: ProgressView = Object.freeze({
  shape: "legacy",
  percent: null,
  stage: null,
  done: false,
  outcome: null,
  error: null,
  headline: null,
  doc: EMPTY_FORECAST,
});

/** One SSE frame, as `parseSSEStream` yields it. */
export interface Frame {
  event: string;
  data: unknown;
}

function asRecord(data: unknown): Record<string, unknown> {
  return data !== null && typeof data === "object" ? (data as Record<string, unknown>) : {};
}

function numberOr(value: unknown, fallback: number | null): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

/**
 * The headline a folded document currently implies.
 *
 * A boolean question's answer is one number; every other space's is mass over
 * its support, which needs the space to name the slots — so both come from the
 * document rather than from a field the producer had to remember to send.
 */
export function headlineOf(doc: ForecastInProgress): Headline | null {
  const forecast = doc.forecast;
  if (!forecast) return null;
  if (forecast.form === "bernoulli") {
    return { kind: "probability", value: forecast.probability };
  }
  return {
    kind: "distribution",
    labels: doc.space ? outcomeLabels(doc.space) : [],
    mass: massOf(forecast),
  };
}

/**
 * Fold one frame into the view. Pure — returns a new view, never mutates.
 *
 * A frame this build does not recognize is ignored rather than thrown on, which
 * is the same bet `parseSSEStream` makes one level down: a producer will ship
 * something new before its consumers know about it, and the run should keep
 * rendering when it does.
 */
export function applyFrame(view: ProgressView, frame: Frame): ProgressView {
  const data = asRecord(frame.data);

  if (frame.event === "update") {
    // `parseUpdate` is total and tolerant: an unrecognized `kind` becomes an
    // UnknownUpdate that `applyUpdate` collects onto `doc.unknown` rather than
    // dropping, so a newer producer degrades to "render it generically".
    const doc = applyUpdate(view.doc, parseUpdate(data));
    return {
      ...view,
      shape: "typed",
      doc,
      // The fold's headline and nothing else: a change of space clears the forecast, and
      // showing the previous one would label old numbers with the new space. None shows
      // until the producer restates one in the new space.
      headline: headlineOf(doc),
      outcome: doc.outcome,
      // A typed run says outright that it is over. The legacy `done` frame is
      // about the TRANSPORT finishing; this is about the forecast finishing,
      // and only one of them distinguishes "completed" from "gave up".
      done: view.done || doc.outcome !== null,
    };
  }

  if (frame.event === "progress") {
    return {
      ...view,
      percent: numberOr(data.progress, view.percent),
      stage: typeof data.stage === "string" ? data.stage : view.stage,
    };
  }

  if (frame.event === "done") {
    // On a legacy producer this payload is the only place the answer appears, so
    // the legacy path keeps the headline it has when the payload carries none. On
    // a typed one the updates already carried it, and re-reading it here would let
    // a coarser field overwrite the richer one (or bring back a headline a change
    // of space cleared), so the fold's headline stands.
    const headline =
      view.shape === "typed"
        ? headlineOf(view.doc)
        : typeof data.probability === "number"
          ? ({ kind: "probability", value: data.probability } as const)
          : view.headline;
    return { ...view, done: true, percent: view.percent ?? 100, headline };
  }

  if (frame.event === "error") {
    return { ...view, done: true, error: String(data.message ?? data.code ?? "unknown error") };
  }

  if (frame.event === "cancelled") {
    return { ...view, done: true };
  }

  return view;
}

/** Fold a whole sequence of frames — the same thing a render loop does. */
export function foldFrames(frames: Iterable<Frame>, from: ProgressView = EMPTY_VIEW): ProgressView {
  let view = from;
  for (const frame of frames) view = applyFrame(view, frame);
  return view;
}
